type QuantitativeValue =
  | { "@type": "QuantitativeValue"; value: number; unitText: string }
  | { "@type": "QuantitativeValue"; minValue: number; maxValue: number; unitText: string };

interface MonetaryAmount {
  "@type": "MonetaryAmount";
  currency: string;
  value: QuantitativeValue;
}

interface BaseSalary {
  baseSalary: MonetaryAmount;
}

export class SalaryUtils {
  /**
   * Parses a raw compensation string and returns a Google Jobs JSON-LD baseSalary object.
   *
   * Supported formats:
   *  - CPM / cents-per-mile: "47CPM", "47 CPM", "47c per mile", "47 cents per mile",
   *                          "$0.47 per mile", ".55 to .64 CPM", "Start pay range .55 to .64 CPM"
   *  - CPM range:            "63 - 66 CPM", "$0.60 to $0.72 per mile", "0.80 - 0.80 per mile"
   *  - Weekly:               "$1,700/week", "$1,700 - $1,900/week", "$1,700 per week"
   *  - Annual:               "$85,000", "$180k per year", "$200,000 per year",
   *                          "$202,000 avg annual"
   *  - Annual range:         "$75,000 - $90,000", "$75k - $90k per year"
   *  - Percentage:           "25%", "70%-80%"
   */
  static parseCompensationToBaseSalary(raw: string): BaseSalary | null {
    if (!raw?.trim()) return null;

    const input = raw.trim();
    const normalised = input.toLowerCase().replace(/\s+/g, " ").trim();

    // ─── Regex fragments ──────────────────────────────────────────────────────
    const AMOUNT    = /\$?([\d,]*\.?\d+k?)/;
    const RANGE_SEP = /\s*(?:to|-)\s*/;
    const CPM_UNIT  = /(?:c(?:ents?)?(?:\s*per\s*mile)?|cpm|(?:cents?\s*)?per\s*mile)/;
    const WEEK_UNIT = /(?:per\s*week|\/\s*week|weekly)/;
    const YEAR_UNIT = /(?:(?:avg|average)\s*annual|per\s*year|\/\s*year|annually|annual)/;

    // ─── 1. Percentage  e.g. "25%", "70%-80%" ────────────────────────────────
    const percentRangeMatch = normalised.match(/(\d+(?:\.\d+)?)\s*%\s*(?:to|-)\s*(\d+(?:\.\d+)?)\s*%/);
    if (percentRangeMatch) {
      const min = parseFloat(percentRangeMatch[1]);
      const max = parseFloat(percentRangeMatch[2]);
      return SalaryUtils._buildResult(SalaryUtils._rangeOrSingle(min, max, "PERCENT"));
    }

    const percentSingleMatch = normalised.match(/(\d+(?:\.\d+)?)\s*%/);
    if (percentSingleMatch) {
      return SalaryUtils._buildResult(SalaryUtils._singleValue(parseFloat(percentSingleMatch[1]), "PERCENT"));
    }

    // ─── 2. CPM / cents-per-mile ──────────────────────────────────────────────
    const cpmRangeRegex = new RegExp(
      AMOUNT.source + `[ck]?` + RANGE_SEP.source + AMOUNT.source + `\\s*` + CPM_UNIT.source
    );
    const cpmRangeMatch = normalised.match(cpmRangeRegex);
    if (cpmRangeMatch) {
      const min = SalaryUtils._normaliseCPM(SalaryUtils._parseAmount(cpmRangeMatch[1]));
      const max = SalaryUtils._normaliseCPM(SalaryUtils._parseAmount(cpmRangeMatch[2]));
      return SalaryUtils._buildResult(SalaryUtils._rangeOrSingle(min, max, "MILE"));
    }

    const cpmSingleRegex = new RegExp(AMOUNT.source + `[ck]?\\s*` + CPM_UNIT.source);
    const cpmSingleMatch = normalised.match(cpmSingleRegex);
    if (cpmSingleMatch) {
      const cpm = SalaryUtils._normaliseCPM(SalaryUtils._parseAmount(cpmSingleMatch[1]));
      return SalaryUtils._buildResult(SalaryUtils._singleValue(cpm, "MILE"));
    }

    // ─── 3. Weekly ────────────────────────────────────────────────────────────
    const weekRangeRegex = new RegExp(
      AMOUNT.source + RANGE_SEP.source + AMOUNT.source + `\\s*` + WEEK_UNIT.source
    );
    const weekRangeMatch = normalised.match(weekRangeRegex);
    if (weekRangeMatch) {
      const min = SalaryUtils._parseAmount(weekRangeMatch[1]);
      const max = SalaryUtils._parseAmount(weekRangeMatch[2]);
      if (min >= 100 && max >= 100) {
        return SalaryUtils._buildResult(SalaryUtils._rangeOrSingle(min, max, "WEEK"));
      }
    }

    const weekSingleRegex = new RegExp(AMOUNT.source + `\\s*` + WEEK_UNIT.source);
    const weekSingleMatch = normalised.match(weekSingleRegex);
    if (weekSingleMatch) {
      const amount = SalaryUtils._parseAmount(weekSingleMatch[1]);
      if (amount >= 100) {
        return SalaryUtils._buildResult(SalaryUtils._singleValue(amount, "WEEK"));
      }
    }

    // ─── 4. Annual ────────────────────────────────────────────────────────────
    const yearRangeRegex = new RegExp(
      AMOUNT.source + RANGE_SEP.source + AMOUNT.source + `(?:\\s*` + YEAR_UNIT.source + `)?`
    );
    const yearRangeMatch = normalised.match(yearRangeRegex);
    if (yearRangeMatch) {
      const min = SalaryUtils._parseAmount(yearRangeMatch[1]);
      const max = SalaryUtils._parseAmount(yearRangeMatch[2]);
      if (min >= 1000 && max >= 1000) {
        return SalaryUtils._buildResult(SalaryUtils._rangeOrSingle(min, max, "YEAR"));
      }
    }

    const yearSingleRegex = new RegExp(AMOUNT.source + `(?:\\s*` + YEAR_UNIT.source + `)?`);
    const yearSingleMatch = normalised.match(yearSingleRegex);
    if (yearSingleMatch) {
      const amount = SalaryUtils._parseAmount(yearSingleMatch[1]);
      if (amount >= 1000) {
        return SalaryUtils._buildResult(SalaryUtils._singleValue(amount, "YEAR"));
      }
    }

    return null;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /** Strip commas, handle "k" suffix (e.g. "180k" → 180000) */
  private static _parseAmount(str: string): number {
    const cleaned = str.replace(/,/g, "").toLowerCase();
    return cleaned.endsWith("k") ? parseFloat(cleaned) * 1000 : parseFloat(cleaned);
  }

  /**
   * Normalise a CPM value to decimal dollars for Google Jobs schema.
   * Whole cent values (e.g. 47) are converted to dollars (0.47).
   * Already-decimal values (e.g. 0.47) are left alone.
   */
  private static _normaliseCPM(val: number): number {
    return val >= 1 ? parseFloat((val / 100).toFixed(4)) : val;
  }

  private static _singleValue(value: number, unitText: string): QuantitativeValue {
    return { "@type": "QuantitativeValue", value, unitText };
  }

  private static _rangeOrSingle(min: number, max: number, unitText: string): QuantitativeValue {
    return min === max
      ? SalaryUtils._singleValue(min, unitText)
      : { "@type": "QuantitativeValue", minValue: min, maxValue: max, unitText };
  }

  private static _buildResult(qv: QuantitativeValue): BaseSalary {
    return {
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "USD",
        value: qv,
      },
    };
  }
}
